/**
 * Arcade telemetry: PostHog (EU) + Sentry, offline queue, every event tagged with the game id.
 * Event names are free-form but must follow docs/events.md conventions:
 *   app_open, level_start, level_end {result: won|lost|skip}, move, guess, share_click, share_done, moment.
 * H10: no session recording, no autocapture, anonymous until an account exists.
 */
import posthog from "posthog-js";
import * as Sentry from "@sentry/browser";
import { currentLang } from "./lang";

type Props = Record<string, string | number | boolean>;
interface Ev {
  name: string;
  ts: number;
  props: Props;
}

const KEY = "arcade.events";
let gameId = "arcade";
let ready = false;
let queue: Ev[] = [];
try {
  queue = JSON.parse(localStorage.getItem(KEY) ?? "[]") as Ev[];
} catch {
  queue = [];
}

export function initTelemetry(id: string): void {
  gameId = id;
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (key && !new URLSearchParams(location.search).has("bot")) {
    posthog.init(key, {
      api_host: (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? "https://eu.i.posthog.com",
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: true,
      persistence: "localStorage+cookie",
      person_profiles: "identified_only",
      disable_session_recording: true,
      disable_surveys: true, // H10: no in-game surveys/popups
      capture_dead_clicks: false,
      capture_heatmaps: false,
      capture_performance: false,
    });
    // lang: the game's language (?lang= → launcher/hub choice → browser), not the browser's; word games play in it.
    posthog.register({ game: id, app: "arcade", lang: currentLang() });
    ready = true;
    for (const e of queue) posthog.capture(e.name, { ...e.props, queued_ts: e.ts });
    queue = [];
    // Sent: drop the stored copy too, or the next page load would send the same events again.
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* private mode */
    }
  }
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (dsn) {
    Sentry.init({ dsn, release: `arcade@${import.meta.env.VITE_APP_VERSION ?? "dev"}`, environment: import.meta.env.MODE, tracesSampleRate: 0.05 });
    Sentry.setTag("game", id);
  }
}

export function track(name: string, props: Props = {}): void {
  const ev: Ev = { name, ts: Date.now(), props: { game: gameId, ...props } };
  if (import.meta.env.DEV) console.debug("[telemetry]", name, ev.props);
  if (ready) {
    posthog.capture(name, ev.props);
    return;
  }
  queue.push(ev);
  try {
    localStorage.setItem(KEY, JSON.stringify(queue.slice(-300)));
  } catch {
    /* private mode */
  }
}

/** Local log (also used by the smoke test to assert events fired). */
export function recentEvents(): string[] {
  return queue.map((e) => e.name);
}
